import {
  WorkerApi,
  type StandardIO,
  destroySharedGraphicsWorker,
  type onLoadResponse,
  type GetAuthToken,
  type RunHangReporter,
} from './WorkerApi';
import {
  KAREL_SLEEP_TIME_BYTE_INDEX,
  encodeKarelSleepByte,
} from './karel/karelSleep';
import type {
  CiPLibrary,
  LangId,
  ProjectFileData,
  GradingScripts,
  StepData,
  RunCodeOptions,
  RunResult,
} from './types/general';
import type {
  GraphicsShape,
  ShapeId,
} from './python/pyodide/GraphicsLib/graphicsTypes';

export type {RunCodeOptions, RunResult} from './types/general';

export interface SockCallbacks {
  stdout?: (output: string) => void;
  stderr?: (error: string) => void;
  stdin?: () => Promise<string>;
  onRunEnd?: (result: RunResult) => void;
  onStep?: (data: StepData) => void;
}

interface BasePlugOptions {
  useWorker?: boolean;
  ioCallbacks?: StandardIO;
  /**
   * Reports each worker's load outcome. `success: false` comes from the
   * worker itself, or from `WorkerApi` when the worker errors or stays silent
   * past its load timeout; a slow worker can still report `success: true`
   * after that. Omitted, load outcomes are ignored.
   */
  onLoadCallback?: (data: onLoadResponse) => void;
  /**
   * Optional async provider invoked once per `runCode` call that sets
   * `RunCodeOptions.attachAuthToken`, to fetch a Bearer token to attach
   * to that run. Other runs carry no token. The token rides
   * `cacheState.idToken` and is read by Pyodide libraries that need to
   * authenticate outbound HTTP calls (e.g. `call_gpt`). Omit when the
   * host has no notion of authenticated users (e.g. math4ai). The
   * package itself stays Firebase-agnostic — callers wire whatever
   * auth source they have. See {@link GetAuthToken}.
   */
  getAuthToken?: GetAuthToken;
  /**
   * Optional Cloud Functions base URL (no trailing slash, no function
   * name) to forward to Pyodide libraries that POST to Cloud
   * Functions. Lets the same wheel target either production or a
   * running Functions emulator depending on host configuration. The
   * webapp builds this from `firebaseConfig.projectId` plus the
   * runtime emulator state (see `@cip/core/firebase`'s
   * `getFunctionsBaseUrl`). Other hosts wire their own. Omit to let
   * Pyodide fall back to its built-in default.
   */
  functionsBaseUrl?: string;
  /**
   * Host callback fired per run-hang watchdog firing. Omit to disable the
   * watchdog entirely. See `runHangWatchdog.ts` for cadence and payload.
   */
  onRunHang?: RunHangReporter;
}

const DEFAULT_BASE_PLUG_OPTIONS: BasePlugOptions = {
  useWorker: true,
  ioCallbacks: {
    stdin: async () => '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    stdout: (s: string) => {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    stderr: (s: string) => {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    onStep: (data: StepData) => {},
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  onLoadCallback: (data: onLoadResponse) => {},
};

export class BasePlug {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  private worker: WorkerApi = null!;
  /**
   * Currently-tracked karel inter-step sleep time, in seconds. Read by
   * the karel pre-run hook (registered lazily on first
   * {@link setKarelSleepTime} call) so every dispatched run sees the
   * latest value, including back-to-back runs after `WorkerApi.FINISHED`
   * clears `controlEventsView`.
   */
  private karelSleepSeconds: number = 0;
  /**
   * Whether the karel sleep pre-run hook has been registered on the
   * underlying `WorkerApi`. The hook is idempotent across consumer
   * calls — `setKarelSleepTime` registers it once on first call and
   * never again, so callers that fire repeatedly (e.g. a slider
   * `onChange`) don't accumulate hooks.
   */
  private karelSleepHookRegistered: boolean = false;
  // private interruptBuffer: Uint8Array = new Uint8Array(new SharedArrayBuffer(1));
  private langId: LangId;
  private setStepDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // constructor( langId: LangId, ioCallbacks: StandardIO = { stdin: async () => '', stdout: (s: string) => {}, stderr: (s: string) => {}, onStep: (data: StepData) => {} }, onLoadCallback: (data: any) => void = (data: any) => {}, useWorker: boolean = true) {
  constructor(
    langId: LangId,
    options: BasePlugOptions = {},
  ) {
    const {
      useWorker,
      ioCallbacks,
      onLoadCallback,
      getAuthToken,
      functionsBaseUrl,
      onRunHang,
    } = { ...DEFAULT_BASE_PLUG_OPTIONS, ...options };
    this.langId = langId;
    if (useWorker) {
      this.worker = new WorkerApi(
        this.langId,
        ioCallbacks,
        // this.interruptBuffer,
        onLoadCallback,
        getAuthToken ?? null,
        functionsBaseUrl ?? null,
        onRunHang ?? null,
      );
    } else {
      onLoadCallback({
        success: true,
      });
    }
  }

  async runCode(code: string, options?: RunCodeOptions): Promise<RunResult> {
    return this.worker.runCode(code, options);
  }

  runTest(
    code: string,
    fileToTest: ProjectFileData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    input: string[] = [],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    gradingScripts: GradingScripts,
  ) {
    throw new Error('Method not implemented.');
  }

  setStep(length: number) {
    if (this.setStepDebounceTimer) {
      clearTimeout(this.setStepDebounceTimer);
    }
    this.setStepDebounceTimer = setTimeout(() => {
      this.worker.setStep(length);
      this.setStepDebounceTimer = null;
    }, 16); // ~60fps debounce
  }

  /**
   * Halt any in-flight execution. Terminates the worker and brings up a
   * fresh one with previously cached APIs re-registered (see
   * `WorkerApi.interruptCode`). Resolves once the worker has been replaced
   * and the run promise has been settled with `interrupted: true`. Setup
   * of the new worker (PYODIDE_READY, REGISTER_API flush) continues
   * asynchronously and is awaited by the next `runCode` call before it
   * posts RUN. No-op when no run is in flight.
   *
   * @returns Promise that resolves when the in-flight run has been torn
   *   down.
   */
  async interrupt(): Promise<void> {
    await this.worker.interruptCode();
  }

  /**
   * Loads the runtime again in a fresh worker after a failed load, keeping
   * registered APIs and callbacks. The outcome is reported through
   * `onLoadCallback`. A run waiting on the failed worker resolves as
   * interrupted. Use {@link interrupt} to stop a run instead.
   *
   * @return Nothing; the load outcome is reported through `onLoadCallback`.
   * @throws Never.
   */
  restart(): void {
    this.worker?.restartWorker();
  }

  /**
   * Subscribe to plug-busy transitions so UI can coordinate across multiple
   * consumers of the shared plug. Fires `true` when a `runCode` starts or
   * queues while another is active, and `false` when the last in-flight or
   * queued run completes.
   *
   * @param listener - Called on every busy transition with the new state.
   * @return Unsubscribe function; invoke to stop receiving updates.
   */
  onRunStatusChange(listener: (busy: boolean) => void): () => void {
    return this.worker.onRunStatusChange(listener);
  }

  /**
   * Whether a `runCode` call is currently in flight or queued against this
   * plug. Useful for one-shot checks without subscribing.
   *
   * @return True if busy, false otherwise.
   */
  isBusy(): boolean {
    return this.worker.isBusy();
  }

  async registerApi(apiRegistration: CiPLibrary) {
    await this.worker.registerApi(apiRegistration);
  }

  registerCallbacks(callbacks: SockCallbacks) {
    this.worker.registerCallbacks(callbacks);
  }

  setCanvas(canvas: HTMLElement, canvasId: string) {
    this.worker.sendCanvas(canvas, canvasId);
  }

  /**
   * Snapshot the current canvas shape map. Resolves to `{}` on
   * no-graphics-worker or ~1s timeout. Returned map is a deep copy.
   */
  async getCanvasState(): Promise<Record<ShapeId, GraphicsShape>> {
    return this.worker.getCanvasState();
  }

  setControlEventsBuffer(index: number, value: number) {
    this.worker.setControlEventsBuffer(index, value);
  }

  /**
   * Set the karel inter-step sleep time. The value persists across runs
   * even though `WorkerApi.FINISHED` clears `controlEventsView` between
   * them — first call lazily registers a pre-run hook that re-asserts
   * the encoded byte before every `runCode` dispatch, and the call also
   * writes the byte immediately so a slider drag during an in-flight
   * run takes effect on the worker's next `time.sleep`.
   *
   * Subsequent calls just update the tracked value; the pre-run hook is
   * registered exactly once for the lifetime of the plug, so callers
   * (e.g. a slider `onChange`) can fire as often as they like without
   * accumulating hooks.
   *
   * Out-of-range inputs are clamped to [0, 1] rather than rejected.
   *
   * Non-karel projects should NOT call this method — byte 0 collides
   * with graphics' mousedown.x in the current control-events layout.
   * Once the cross-library byte schema in
   * [#2470](https://github.com/CodeinPlace/cip/issues/2470) lands, the
   * karel/graphics overlap goes away and this caveat will too.
   *
   * @param seconds Inter-step sleep time, clamped to the closed
   *   interval [0, 1].
   */
  setKarelSleepTime(seconds: number): void {
    this.karelSleepSeconds = seconds;
    if (!this.worker) {
      return;
    }
    // Immediate write covers the mid-run-drag case: the worker reads
    // controlEventsView on its next `time.sleep`, so updating the byte
    // now means a slider change between worker reads takes effect
    // without waiting for the next pre-run hook.
    this.worker.setControlEventsBuffer(
      KAREL_SLEEP_TIME_BYTE_INDEX,
      encodeKarelSleepByte(seconds),
    );
    if (this.karelSleepHookRegistered) {
      return;
    }
    // Register lazily, not eagerly in the constructor: byte 0 of the
    // control-events buffer is shared with graphics' mousedown.x, so a
    // hook that always fires would clobber graphics input on every
    // non-karel run. Gating on first setKarelSleepTime call means only
    // plugs that have opted into karel pay the byte-0 write. (Once
    // #2470 namespaces byte indices, this can move to eager init.)
    // The hook reads `this.karelSleepSeconds` fresh on each firing so
    // the latest setKarelSleepTime call wins on every subsequent run.
    this.karelSleepHookRegistered = true;
    this.worker.addPreRunHook(() => {
      this.worker.setControlEventsBuffer(
        KAREL_SLEEP_TIME_BYTE_INDEX,
        encodeKarelSleepByte(this.karelSleepSeconds),
      );
    });
  }

  /**
   * Read the karel inter-step sleep time the plug is currently
   * tracking. Useful for save/restore patterns — e.g. the karel
   * unit-test runner overrides to 0 for instant playback during
   * grading, then restores the previous value when the test loop
   * finishes.
   *
   * @return The seconds value last passed to {@link setKarelSleepTime},
   *   or 0 if never set.
   */
  getKarelSleepTime(): number {
    return this.karelSleepSeconds;
  }

  /**
   * Register a hook that fires synchronously immediately before every
   * `runCode` posts RUN to the worker. Used by library-specific
   * bindings (notably the karel sleep binding in `karel/karelSleep.ts`)
   * to re-assert control-events bytes that `WorkerApi` clears on
   * FINISHED, without forcing every caller of `runCode` to know about
   * the byte.
   *
   * @param hook - Synchronous callback invoked once per run dispatch on
   *   this plug.
   * @return Unsubscribe function; invoke to stop receiving invocations.
   *   No-op when this plug was constructed with `useWorker: false`.
   */
  addPreRunHook(hook: () => void): () => void {
    if (!this.worker) {
      return () => {};
    }
    return this.worker.addPreRunHook(hook);
  }

  clearReplState() {
    this.worker.clearReplState();
  }

  dispose(graphicsWorker: boolean = false) {
    if (this.setStepDebounceTimer) {
      clearTimeout(this.setStepDebounceTimer);
      this.setStepDebounceTimer = null;
    }
    this.worker.terminate();
    if (graphicsWorker) {
      destroySharedGraphicsWorker();
    }
  }
}
